import React, { useEffect, useMemo, useState } from 'react';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { isActivePath } from './helpers';

const Submenu = ({ title, items, navigate, open: sidebarOpen = true, pathname = '' }) => {
  const theme = useTheme();

  const COLORS = useMemo(
    () => ({
      text: theme.palette.text.primary,
      textMuted: theme.palette.text.secondary,
      hover: theme.palette.action.hover,
      accent: theme.palette.primary.main,
      accentDim: alpha(theme.palette.primary.main, 0.22),

      headerBg: alpha(theme.palette.primary.main, 0.10),
      itemActiveBg: alpha(theme.palette.primary.main, 0.12),
      itemActiveOutline: alpha(theme.palette.primary.main, 0.28),
    }),
    [theme]
  );

  const hasActiveItem = useMemo(
    () => items.some((it) => pathname && it.path && isActivePath(pathname, it.path)),
    [items, pathname]
  );

  const [expanded, setExpanded] = useState(hasActiveItem);

  useEffect(() => {
    if (hasActiveItem) setExpanded(true);
  }, [hasActiveItem]);

  const handleToggleClick = () => sidebarOpen && setExpanded((v) => !v);

  return (
    <List sx={{ p: 0, m: 0 }}>
      <ListItemButton
        onClick={handleToggleClick}
        sx={{
          position: 'relative',
          color: COLORS.text,
          px: 2.0,
          py: 0.9,              // tighter
          m: '6px 10px',         // tighter
          borderRadius: 1.5,
          transition: 'background .18s ease, box-shadow .18s ease',
          '&:hover': { backgroundColor: COLORS.hover },
          ...(hasActiveItem && {
            backgroundColor: COLORS.headerBg,
            boxShadow: `inset 0 0 0 1px ${COLORS.accentDim}`,
          }),
        }}
        disabled={!sidebarOpen}
      >
        {/* left active bar for group */}
        <Box
          sx={{
            position: 'absolute',
            left: 6,
            top: 8,
            bottom: 8,
            width: 3,
            borderRadius: 2,
            backgroundColor: hasActiveItem ? COLORS.accent : 'transparent',
          }}
        />

        <ListItemText
          primary={title}
          primaryTypographyProps={{
            fontWeight: hasActiveItem ? 800 : 650,
            letterSpacing: 0.2,
            fontSize: 13,
            color: hasActiveItem ? COLORS.text : COLORS.textMuted,
          }}
        />
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>

      <Collapse in={expanded && sidebarOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pb: 0.25 }}>
          {items.map((item, index) => {
            const active = pathname && item.path && isActivePath(pathname, item.path);
            const handleClick = () => {
              if (item.action) return item.action();
              if (item.path) return navigate(item.path);
            };

            return (
              <ListItemButton
                key={index}
                onClick={handleClick}
                sx={{
                  position: 'relative',
                  pl: 2.25,
                  pr: 1.25,
                  py: 0.75,            // tighter
                  m: '3px 10px',        // tighter
                  borderRadius: 1.5,
                  color: COLORS.text,
                  transition: 'background .18s ease, box-shadow .18s ease',
                  '&:hover': { backgroundColor: COLORS.hover },

                  ...(active && {
                    backgroundColor: COLORS.itemActiveBg,
                    boxShadow: `inset 0 0 0 1px ${COLORS.itemActiveOutline}`,
                  }),
                }}
              >
                {/* active bar for item */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 6,
                    top: 8,
                    bottom: 8,
                    width: 3,
                    borderRadius: 2,
                    backgroundColor: active ? COLORS.accent : 'transparent',
                  }}
                />

                <ListItemIcon
                  sx={{
                    color: active ? COLORS.accent : COLORS.textMuted,
                    minWidth: 30,
                    '& svg': { fontSize: 20 },
                  }}
                >
                  {item.icon}
                </ListItemIcon>

                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: 13.5,
                    fontWeight: active ? 750 : 520,
                    letterSpacing: 0.2,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
    </List>
  );
};

export default Submenu;
